import { motion } from "framer-motion";
import { TrendingUp, DollarSign, Users, AlertTriangle } from "lucide-react";
import { useShouldReduceAnimations } from "@/hooks/useReducedMotion";

const Problem = () => {
  const reduceAnimations = useShouldReduceAnimations();
  const stats = [
    {
      icon: DollarSign,
      stat: "300%",
      label: "Google Ads costs have tripled in 5 years",
    },
    {
      icon: Users,
      stat: "100M+",
      label: "People now use AI assistants daily",
    },
    {
      icon: TrendingUp,
      stat: "47%",
      label: "Of searches will shift to AI by 2026",
    },
  ];

  return (
    <section className="py-12 md:py-20 relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary/30 to-transparent" />
      
      <div className="container mx-auto px-4 max-w-[1280px] relative">
        <motion.div
          initial={reduceAnimations ? { opacity: 1 } : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: reduceAnimations ? 0 : 0.4 }}
          className="max-w-4xl mx-auto text-center space-y-6"
        >
          <div className="inline-flex items-center gap-2 bg-destructive/10 border border-destructive/20 rounded-full px-4 py-2 text-sm">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <span className="text-foreground uppercase tracking-wider text-xs font-semibold">The Problem You're Facing</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            Google Ads Are{" "}
            <span className="text-destructive">Bleeding Your Budget</span>
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            While you're fighting over expensive Google keywords, your competitors are 
            ignoring the biggest shift in how people search since the iPhone.
          </p>

          <div className="grid md:grid-cols-3 gap-6 pt-6">
            {stats.map((item, index) => (
              <motion.div
                key={index}
                initial={reduceAnimations ? { opacity: 1 } : { opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: reduceAnimations ? 0 : 0.3, delay: reduceAnimations ? 0 : index * 0.05 }}
                className="glass rounded-2xl p-6 text-center hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all"
              >
                <item.icon className="h-8 w-8 text-accent mx-auto mb-4" />
                <div className="text-3xl md:text-4xl font-bold text-accent mb-2">
                  {item.stat}
                </div>
                <p className="text-sm text-muted-foreground">{item.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={reduceAnimations ? { opacity: 1 } : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: reduceAnimations ? 0 : 0.4, delay: reduceAnimations ? 0 : 0.2 }}
            className="pt-6"
          >
            <p className="text-xl md:text-2xl font-semibold">
              Your customers are asking <span className="text-accent">ChatGPT</span> instead of Googling.
              <br />
              <span className="text-gradient">Are you showing up?</span>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Problem;
